import React from 'react';

import type { CellProps, Row } from 'react-table';
import { Tooltip } from '@bringg/react-components';
import { secondsInTimeFormat } from '@bringg/react-components/dist/services/filters';

import { getActionableWayPoint, isRouteExistsAndUnassigned } from '../../../routes-planner/utils';
import RouteRow from '../route-row/route-row';
import { NO_ROUTE_ID } from '../../../../stores/routes-planner-store';
import { addDecimal } from '../../../../utils/numbers-utils';
import type { RoutesPlannerDeps, RoutesPlannerTask } from '../../../../types';
import MaxHandlingUnits from '../../handling-units/max-handling-units';
import { getHoursFormat } from '../../../../utils/date-utils';
import TimeColumn from './columns/time-column';
import TaskSelectColumn from './columns/task-select-columns';
import type { UpdatingSource } from '../../../types';
import PriorityColumn from './columns/priority-column';
import PickupDropoffColumn from './columns/way-points-column';
import ColorMarkColumn from './columns/color-mark-column';
import { TABLE_COLUMNS, TABLE_STATIC_COLUMNS } from '../../table-columns';
import useStore from '../../../../stores/helpers/use-context';
import CustomerColumn from './columns/customer-column/customer-column';
import CustomAttributeColumn from './columns/custom-attribute-column';

const runIdSortFn = (rowA: Row, rowB: Row, columnId: string) => {
	const runIdA = rowA.values[columnId];
	const runIdB = rowB.values[columnId];
	if (runIdA === NO_ROUTE_ID) return -1;

	return runIdA - runIdB;
};

export interface TableColumnsProps {
	deps: RoutesPlannerDeps;
	selectedTasksIds: number[];
	changeTasksSelection: (tasksIds: number[], source: UpdatingSource) => void;
	multiSelectedEnabled: boolean;
}

const useTableColumns = () => {
	const {
		translations,
		runToTasksInPresentedDate,
		routesById,
		runToDriver,
		setRouteColor,
		routesColors,
		allConfiguration,
		team,
		vehicleTypeByVehicleIds,
		routeInfoByIndex,
		openUserAssignerModal,
		openVehicleAssignerModal,
		optimizationType,
		customerIdByTaskInventory,
		vehiclesById,
		shouldShowVehicles,
		shouldShowTrailer,
		isRunModified,
		deliveryBlocksByDriver,
		deliveryBlocksByVehicle,
		tasksById,
		tableStore,
		customAttributes
	} = useStore();
	const { presentedColumns, customerAvailabilityHours } = tableStore;

	const { routesTableTranslations } = translations;

	const getTableColumns = ({
		deps,

		selectedTasksIds,
		changeTasksSelection,
		multiSelectedEnabled
	}: TableColumnsProps) => {
		const { enableVehicles, hourFormat, enableTaskPrice, enableSkills } = allConfiguration;
		const { dynamicAggregationsTranslations } = translations;

		const tableColumns = [
			{
				id: TABLE_STATIC_COLUMNS.colorMark.id,
				width: 6,
				priority: TABLE_STATIC_COLUMNS.colorMark.priority,
				accessor: row => {
					return isRouteExistsAndUnassigned(routesById[row.run_id], optimizationType)
						? 'white'
						: routesColors.get(row.run_id);
				},
				Cell: ({ value: color, row }: CellProps<RoutesPlannerTask>) => {
					const taskStatus = row.original.status;
					return <ColorMarkColumn taskStatus={taskStatus} color={color} />;
				}
			},
			{
				accessor: 'run_id',
				sortType: runIdSortFn,
				Group: (props: CellProps<RoutesPlannerTask>) => {
					const { value: runId, row } = props;

					return (
						<RouteRow
							cellProps={props}
							selectedTasksIds={selectedTasksIds}
							tasksById={tasksById}
							changeTasksSelection={changeTasksSelection}
							routesById={routesById}
							optimizationType={optimizationType}
							allConfiguration={allConfiguration}
							name={routesById[runId]?.title || routesTableTranslations.groupHeaders.unassignedOrders}
							runToDriver={runToDriver}
							color={routesColors.get(runId)}
							headers={routesTableTranslations.headers}
							isExpanded={row.isExpanded}
							checkListAmount={row.leafRows.length}
							translations={{
								...routesTableTranslations.headers,
								...routesTableTranslations.actions,
								...dynamicAggregationsTranslations
							}}
							onColorChange={(color: string) => setRouteColor(runId, color)}
							customerIdByTaskInventory={customerIdByTaskInventory}
							runToTasksInPresentedDate={runToTasksInPresentedDate}
							routeInfoByIndex={routeInfoByIndex}
							deliveryBlocksByDriver={deliveryBlocksByDriver}
							deliveryBlocksByVehicle={deliveryBlocksByVehicle}
							openUserAssignerModal={openUserAssignerModal}
							openVehicleAssignerModal={openVehicleAssignerModal}
							timezone={team.time_zone}
							vehiclesById={vehiclesById}
							shouldShowVehicles={shouldShowVehicles}
							shouldShowTrailer={shouldShowTrailer}
							deps={deps}
							vehicleTypeByVehicleIds={vehicleTypeByVehicleIds}
							isRunModified={isRunModified}
						/>
					);
				}
			},
			{
				Header: '#',
				accessor: TABLE_COLUMNS.priority.id,
				priority: presentedColumns.priority?.priority,
				width: 40,
				Cell: ({ value, row }) => {
					return <PriorityColumn row={row} value={value} translations={translations} />;
				}
			},
			{
				Header: routesTableTranslations.headers.orderType,
				priority: presentedColumns.way_points?.priority,
				accessor: TABLE_COLUMNS.way_points.id,
				width: 80,
				Cell: ({ value }) => {
					return (
						<PickupDropoffColumn
							translations={translations}
							pickupDropOffOption={value[0].pickup_dropoff_option}
						/>
					);
				}
			},
			{
				Header: routesTableTranslations.headers.orderId,
				priority: presentedColumns.external_id?.priority,
				accessor: TABLE_COLUMNS.external_id.id,
				Cell: ({ value }) => {
					return (
						<Tooltip title={value}>
							<span data-test-id="external-id-cell">{value}</span>
						</Tooltip>
					);
				}
			},
			{
				Header: routesTableTranslations.headers.customerName,
				priority: presentedColumns.customerName?.priority,
				id: TABLE_COLUMNS.customerName.id,
				accessor: row => {
					return getActionableWayPoint(row);
				},
				Cell: ({ value }) => {
					return (
						<CustomerColumn
							wayPoint={value}
							customerAvailabilityHours={customerAvailabilityHours}
							translation={translations.routesTableTranslations}
							endOfWeekDay={allConfiguration.endOfWeekDay}
							timeFormat={allConfiguration.hourFormat}
						/>
					);
				}
			},
			{
				Header: routesTableTranslations.headers.eta,
				priority: presentedColumns.eta?.priority,
				width: 60,
				id: TABLE_COLUMNS.eta.id,
				Cell: a => {
					const eta = a.row.values.way_points[0].eta;
					return (
						<span data-test-id="eta-cell">
							{eta ? getHoursFormat(eta, team.time_zone, hourFormat) : '-'}
						</span>
					);
				}
			},
			{
				Header: routesTableTranslations.headers.timeWindow,
				priority: presentedColumns.timeWindow?.priority,
				id: TABLE_COLUMNS.timeWindow.id,
				width: 130,
				accessor: row => {
					const { no_earlier_than, no_later_than } = getActionableWayPoint(row);

					return [no_earlier_than, no_later_than];
				},
				Cell: ({ value }) => (
					<TimeColumn
						startTime={value[0]}
						endTime={value[1]}
						hourFormat={hourFormat}
						timeZone={team.time_zone}
					/>
				)
			},
			{
				Header: routesTableTranslations.headers.etos,
				priority: presentedColumns.etos?.priority,
				width: 80,
				id: TABLE_COLUMNS.etos.id,
				accessor: row => {
					const { etos } = getActionableWayPoint(row);
					return etos ? secondsInTimeFormat(etos) : '';
				}
			},
			{
				Header: routesTableTranslations.headers.weight,
				priority: presentedColumns.total_weight?.priority,
				id: TABLE_COLUMNS.total_weight.id,
				accessor: 'aggregations.max_weight',
				Cell: ({ value }) => {
					return value ? <span data-test-id="total-weight-cell">{addDecimal(value)}</span> : null;
				},
				width: 90
			},
			{
				Header: routesTableTranslations.headers.sla,
				priority: presentedColumns.sla?.priority,
				id: TABLE_COLUMNS.sla.id,
				width: 180,
				accessor: row => {
					const { first_attempt_promise_no_earlier_than, first_attempt_promise_no_later_than } =
						getActionableWayPoint(row);
					return (
						<TimeColumn
							startTime={first_attempt_promise_no_earlier_than}
							endTime={first_attempt_promise_no_later_than}
							hourFormat={hourFormat}
							timeName="sla"
							timeZone={team.time_zone}
						/>
					);
				}
			},
			{
				Header: routesTableTranslations.headers.rank,
				priority: presentedColumns.rank?.priority,
				id: TABLE_COLUMNS.rank.id,
				accessor: 'rank',
				width: 60
			}
		];

		if (enableVehicles) {
			tableColumns.push({
				Header: routesTableTranslations.headers.handlingUnits,
				priority: presentedColumns.max_handling_units?.priority,
				id: TABLE_COLUMNS.max_handling_units.id,
				width: 150,
				accessor: `aggregations.${TABLE_COLUMNS.max_handling_units.id}`,
				Cell: ({ value }) => <MaxHandlingUnits maxHandlingUnits={value} />
			});
		}

		if (enableTaskPrice) {
			tableColumns.push({
				Header: routesTableTranslations.headers.totalPrice,
				priority: presentedColumns.totalPrice?.priority,
				id: TABLE_COLUMNS.totalPrice.id,
				accessor: 'total_price',
				width: 100
			});
		}

		if (enableSkills) {
			tableColumns.push({
				Header: routesTableTranslations.headers.skills,
				priority: presentedColumns.skills?.priority,
				id: TABLE_COLUMNS.skills.id,
				width: 150,
				accessor: 'required_skills',
				Cell: ({ value }) => {
					return (
						<Tooltip title={value?.join(', ') || ''}>
							<span data-test-id="required-skills-cell">{value?.join(', ') || null}</span>
						</Tooltip>
					);
				}
			});
		}

		customAttributes.forEach(attribute => {
			if (presentedColumns[attribute.id]) {
				tableColumns.push({
					Header: attribute.name,
					priority: presentedColumns[attribute.id].priority,
					width: 100,
					accessor: attribute.path,
					Cell: ({ row }) => {
						return (
							<CustomAttributeColumn
								value={row.values[attribute.path]}
								translation={translations.routesTableTranslations}
							/>
						);
					}
				});
			}
		});

		if (multiSelectedEnabled) {
			tableColumns.push({
				accessor: TABLE_STATIC_COLUMNS.id.id,
				priority: TABLE_STATIC_COLUMNS.id.priority,
				Header: '',
				width: 40,
				Cell: ({ row }) => {
					return (
						<TaskSelectColumn
							row={row}
							selectedTasksIds={selectedTasksIds}
							tasksById={tasksById}
							changeTasksSelection={changeTasksSelection}
						/>
					);
				}
			} as any);
		}

		const maxPriority = Object.keys(TABLE_STATIC_COLUMNS).length + Object.keys(TABLE_COLUMNS).length + 1;
		return tableColumns.sort((a, b) => (a.priority || maxPriority) - (b.priority || maxPriority));
	};
	return { getTableColumns };
};

export default useTableColumns;
